// @cogo/template — slot value + declaration validation
//
// PURE leaf module: no node:fs, no network, no Date.now/Math.random, no
// throwing. Both entry points return `TypedFailure[]` (empty = valid)
// instead of throwing — callers (LLM retry loops, CLI, CI gates) need a
// structured, enumerable failure list rather than a single thrown error.
//
// Two distinct concerns:
//  - `validateSlotValues` (instantiation-side): given a proposed value map,
//    check it against the package's slot declarations (type/constraint/
//    required rules). Never touches an AppBundle.
//  - `validateSlotDecls` (authoring-side): given the *reference* AppBundle
//    the package was authored against, check that every declared address
//    actually resolves to real content and doesn't target a forbidden
//    (presentation/behavior) position. Deliberately does NOT reuse
//    `maskBundle` (canonical.ts) for resolution — `maskBundle` throws
//    TypeError on an unresolved address, which is exactly the failure mode
//    this function must report as data, not raise.

import type { AppBundle, Json, SlotAddress, SlotDecl, TemplatePackage, TypedFailure } from './types'

const ELEMENT_FIELDS = ['text', 'src', 'alt', 'placeholder', 'label'] as const

// Mirrors packages/cogo-schema/v4/template.cogo.schema.json's $defs.address
// record-list `file` pattern — the only two file shapes a record-list may
// ever address. This is what re-establishes, at the file-name level, the
// same "behavior files are categorically unreachable" guarantee the old
// hardcoded `blocks/<blockId>/data.json` path construction gave for free;
// now that `file` is a free string field, it must be checked explicitly.
const RECORD_LIST_FILE_RE = /^(blocks\/[a-z0-9][a-z0-9-]*\/data\.json|data\/[a-z0-9][a-z0-9-]*\.json)$/

// Field/pointer-token names that mark a position as styling/layout content
// rather than templatizable data — kept intentionally small and explicit
// (no fuzzy matching) so the check is predictable to authors.
const PRESENTATION_KEYS = new Set(['className', 'class', 'style', 'styling', 'designTokens', 'layout', 'spacing'])

// Byte-identical mirror of packages/cogo-schema/v4/template.cogo.schema.json's
// $defs.address (app kind) `pointer` pattern. THE SCHEMA IS THE AUTHORITY —
// if that pattern ever changes, update this constant to match. Without this
// check, `forbiddenPosition` only rejected app pointers whose last token was
// a presentation key, so a resolvable-but-off-allowlist pointer like
// `/version` (app configuration, not templatizable content) would pass the
// TS validator while an Ajv-validated package would reject it — a
// close-by-construction gap between the two contract surfaces.
const APP_POINTER_ALLOWLIST_RE = /^\/(name|description|navigation\/[0-9]+\/label)$/

function isPlainObject(value: unknown): value is { [k: string]: Json } {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

// ---------------------------------------------------------------------------
// validateSlotValues — instantiation-side
// ---------------------------------------------------------------------------

/** Returns a human-readable defect description, or `null` if `value` satisfies `slot`'s type + constraint. */
function describeValueDefect(slot: SlotDecl, value: Json): string | null {
  const c = slot.constraint
  switch (slot.type) {
    case 'text':
    case 'richText': {
      if (typeof value !== 'string') return 'must be a string'
      if (c?.maxLength !== undefined && value.length > c.maxLength) return `exceeds maxLength ${c.maxLength}`
      if (c?.pattern !== undefined) {
        let re: RegExp
        try {
          re = new RegExp(c.pattern)
        } catch {
          return `constraint.pattern "${c.pattern}" is not a valid regular expression`
        }
        if (!re.test(value)) return `does not match pattern ${c.pattern}`
      }
      return null
    }
    case 'image':
    case 'datasource-endpoint': {
      if (typeof value !== 'string') return 'must be a URL string'
      try {
        new URL(value)
      } catch {
        return 'must be a valid, parseable URL'
      }
      return null
    }
    case 'number':
    case 'currency': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return 'must be a finite number'
      return null
    }
    case 'record-list': {
      if (!Array.isArray(value)) return 'must be an array'
      if (!value.every(isPlainObject)) return 'every record must be an object'
      const min = c?.minCount ?? 0
      const max = c?.maxCount ?? Infinity
      if (value.length < min || value.length > max) {
        return `length ${value.length} is outside bounds ${min}..${max === Infinity ? 'unbounded' : max}`
      }
      if (c?.recordSchema !== undefined && isPlainObject(c.recordSchema)) {
        const schemaKeys = Object.keys(c.recordSchema).sort().join(',')
        for (const record of value as { [k: string]: Json }[]) {
          const recordKeys = Object.keys(record).sort().join(',')
          // v1: record keys must be exactly the recordSchema keys (no
          // subset/superset leniency) — simplest rule that catches both
          // missing-required-field and unexpected-extra-field authoring bugs.
          if (recordKeys !== schemaKeys) return `record keys [${recordKeys}] must exactly match recordSchema keys [${schemaKeys}]`
        }
      }
      return null
    }
    case 'brand': {
      // Brand slots are ordinary slots that happen to share a `group` tag
      // (applied together by callers); there is no aggregate brand value to
      // validate here — each decl instance is just a plain string slot.
      if (typeof value !== 'string') return 'must be a string'
      return null
    }
  }
}

export function validateSlotValues(pkg: TemplatePackage, values: Record<string, Json>): TypedFailure[] {
  const failures: TypedFailure[] = []
  const bySlotId = new Map(pkg.slots.map((s) => [s.slotId, s]))

  for (const key of Object.keys(values)) {
    if (!bySlotId.has(key)) {
      failures.push({ code: 'slot-value-invalid', message: `unknown slot id "${key}"`, slotId: key })
    }
  }

  for (const slot of pkg.slots) {
    const hasValue = Object.prototype.hasOwnProperty.call(values, slot.slotId)
    if (!hasValue) {
      // sourceValue is a mandatory field on SlotDecl; `null` is the sentinel
      // for "no fallback available" (every real proposed slot carries its
      // original bundle content as a non-null sourceValue — see
      // propose-slots.ts). Missing + required + no fallback is the only
      // failure case; missing + (not required OR has a fallback) silently
      // falls back to sourceValue at instantiation time.
      if (slot.required && slot.sourceValue === null) {
        failures.push({
          code: 'slot-required-missing',
          message: `slot "${slot.slotId}" is required and has no sourceValue fallback`,
          slotId: slot.slotId,
        })
      }
      continue
    }
    const defect = describeValueDefect(slot, values[slot.slotId])
    if (defect !== null) {
      failures.push({ code: 'slot-value-invalid', message: `slot "${slot.slotId}" ${defect}`, slotId: slot.slotId })
    }
  }

  return failures
}

// ---------------------------------------------------------------------------
// validateSlotDecls — authoring-side (address resolution against a bundle)
// ---------------------------------------------------------------------------

function unescapePointerToken(token: string): string {
  return token.replace(/~1/g, '/').replace(/~0/g, '~')
}

function splitPointerTokens(pointer: string): string[] | null {
  if (pointer === '') return []
  if (!pointer.startsWith('/')) return null
  return pointer.slice(1).split('/').map(unescapePointerToken)
}

/** Strict JSON Pointer read: every token — including the last — must already exist. */
function getAtPointer(root: Json, pointer: string): { ok: true; value: Json } | { ok: false } {
  const tokens = splitPointerTokens(pointer)
  if (tokens === null) return { ok: false }
  let cur: Json = root
  for (const token of tokens) {
    if (Array.isArray(cur)) {
      if (!/^(0|[1-9][0-9]*)$/.test(token)) return { ok: false }
      const idx = Number(token)
      if (idx >= cur.length) return { ok: false }
      cur = cur[idx]
      continue
    }
    if (!isPlainObject(cur) || !Object.prototype.hasOwnProperty.call(cur, token)) return { ok: false }
    cur = cur[token]
  }
  return { ok: true, value: cur }
}

/** Preorder search for a plain-object node whose `id` field equals `elementId`. */
function findElementId(node: Json, elementId: string): boolean {
  if (Array.isArray(node)) return node.some((item) => findElementId(item, elementId))
  if (!isPlainObject(node)) return false
  if (node.id === elementId) return true
  return Object.keys(node).some((key) => findElementId(node[key], elementId))
}

function addressKey(address: SlotAddress): string {
  switch (address.kind) {
    case 'element':
      return `element:${address.blockId}:${address.elementId}:${address.field}`
    case 'app':
      return `app:${address.pointer}`
    case 'datasource':
      return `datasource:${address.datasourceId}:${address.field}`
    case 'record-list':
      return `record-list:${address.file}:${address.pointer}`
  }
}

type ForbiddenCode = 'template-presentation-slot-forbidden' | 'template-behavior-slot-forbidden'

/**
 * Structural forbidden-position check, run BEFORE resolution (a forbidden
 * address is an authoring mistake independent of whether it happens to
 * resolve). Returns `null` when the address's position is allowed.
 *
 * Reachability note: `app` addresses are hardcoded (see canonical.ts's
 * `applyMask`) to app.json. `record-list` addresses carry their own `file`
 * field, but `resolvesInBundle` below gates it against
 * `RECORD_LIST_FILE_RE` (mirroring the JSON schema's `file` pattern) —
 * flow.json/events.json/state.json (the "behavior files") are categorically
 * unreachable through either address kind, so an address like
 * `{kind:'record-list', file:'blocks/hero/flow.json', pointer:'/flows'}`
 * simply fails to resolve (-> `slot-address-missing`) rather than being
 * classified as behavior-forbidden here. Only a pointer's *final* token is
 * checked against the presentation-key list (e.g. "into className"), and
 * only `element` addresses (whose `field` names a concrete UI concept) can
 * ever produce `template-behavior-slot-forbidden`.
 */
function forbiddenPosition(address: SlotAddress): ForbiddenCode | null {
  if (address.kind === 'element') {
    if ((ELEMENT_FIELDS as readonly string[]).includes(address.field)) return null
    return PRESENTATION_KEYS.has(address.field) ? 'template-presentation-slot-forbidden' : 'template-behavior-slot-forbidden'
  }
  if (address.kind === 'app') {
    const tokens = splitPointerTokens(address.pointer)
    const last = tokens !== null && tokens.length > 0 ? tokens[tokens.length - 1] : undefined
    if (last !== undefined && PRESENTATION_KEYS.has(last)) return 'template-presentation-slot-forbidden'
    // A resolvable app.json pointer outside the schema's content allowlist
    // (e.g. `/version`) addresses app configuration rather than
    // presentation — the closed failure-code set has no dedicated code for
    // "off-allowlist", so this is classified as behavior-adjacent, per the
    // locked decision (see APP_POINTER_ALLOWLIST_RE above).
    if (!APP_POINTER_ALLOWLIST_RE.test(address.pointer)) return 'template-behavior-slot-forbidden'
    return null
  }
  if (address.kind === 'record-list') {
    const tokens = splitPointerTokens(address.pointer)
    const last = tokens !== null && tokens.length > 0 ? tokens[tokens.length - 1] : undefined
    if (last !== undefined && PRESENTATION_KEYS.has(last)) return 'template-presentation-slot-forbidden'
    return null
  }
  // 'datasource': the type only ever declares field:'baseUrl'; a mismatched
  // field is neither presentation nor behavior, so it is left to fail
  // resolution below (slot-address-missing) instead of being misclassified.
  return null
}

/** Whether `address` resolves to an existing value in `bundle` (no I/O, no throw). */
function resolvesInBundle(bundle: AppBundle, address: SlotAddress): boolean {
  switch (address.kind) {
    case 'element': {
      const tree = bundle.files[`blocks/${address.blockId}/ui.json`]
      if (tree === undefined) return false
      return findElementId(tree, address.elementId)
    }
    case 'app': {
      const appJson = bundle.files['app.json']
      if (appJson === undefined) return false
      return getAtPointer(appJson, address.pointer).ok
    }
    case 'datasource': {
      // `app.json`'s `datasources` is a canonical OBJECT MAP keyed by
      // datasourceId — NOT an array of `{id, baseUrl}` records.
      const appJson = bundle.files['app.json']
      if (!isPlainObject(appJson)) return false
      const datasources = appJson['datasources']
      if (!isPlainObject(datasources)) return false
      const ds = datasources[address.datasourceId]
      if (!isPlainObject(ds)) return false
      return address.field === 'baseUrl' && typeof ds['baseUrl'] === 'string'
    }
    case 'record-list': {
      if (!RECORD_LIST_FILE_RE.test(address.file)) return false
      const dataJson = bundle.files[address.file]
      if (dataJson === undefined) return false
      return getAtPointer(dataJson, address.pointer).ok
    }
  }
}

export function validateSlotDecls(pkg: TemplatePackage, bundle: AppBundle): TypedFailure[] {
  const failures: TypedFailure[] = []
  // address key -> slotId that first claimed it (duplicate-address detection).
  const claimedBy = new Map<string, string>()

  for (const slot of pkg.slots) {
    const addresses = Array.isArray(slot.address) ? slot.address : [slot.address]
    for (const address of addresses) {
      const key = addressKey(address)
      const priorSlotId = claimedBy.get(key)
      if (priorSlotId !== undefined) {
        // The closed failure-code set (§7) has no dedicated "duplicate
        // address" code. Two decls addressing the same position is not an
        // unresolvable address per se, but it is an equally invalid decl
        // (ambiguous which slot's value wins at instantiation) — deliberate
        // reuse of `slot-address-missing` here per the locked decision;
        // the message names it explicitly so it isn't confused with a
        // genuine resolution failure.
        failures.push({
          code: 'slot-address-missing',
          message: `slot "${slot.slotId}" duplicate address: already claimed by slot "${priorSlotId}"`,
          slotId: slot.slotId,
        })
        continue
      }
      claimedBy.set(key, slot.slotId)

      const forbidden = forbiddenPosition(address)
      if (forbidden !== null) {
        failures.push({
          code: forbidden,
          message: `slot "${slot.slotId}" targets a forbidden ${forbidden === 'template-presentation-slot-forbidden' ? 'presentation' : 'behavior'} position`,
          slotId: slot.slotId,
        })
        continue
      }

      if (!resolvesInBundle(bundle, address)) {
        failures.push({
          code: 'slot-address-missing',
          message: `slot "${slot.slotId}" address does not resolve in the bundle`,
          slotId: slot.slotId,
        })
      }
    }
  }

  return failures
}
